import { MouseEvent } from "react"
import { GridColDef } from "@mui/x-data-grid-pro"
import { Box, IconButton, Link, Tooltip, Typography } from "@mui/material"
import { StudentAcademicStatusDTO } from "../../studentAcademicStatus.dto"
import { ColumnKeys } from "./column.keys"
import { store } from "@app/store"
import { showErrorSnackbar, showSuccessSnackbar } from "@features/ui/uiSlice"
import { hubspotContactUrl } from "../studentsTable.actions"
import AppIcon from "@utils/appIcon"
import appIcons from "@utils/appIcons"

// Copying an email is a daily job (tickets, escalations) — one hover-revealed
// click on the row, no separate Email column needed. The toast names the
// student so a copy during a fast scan is verifiable.
const copyEmail = (event: MouseEvent, name: string, email: string) => {
  // The row click toggles the detail panel; a copy must not.
  event.stopPropagation()
  navigator.clipboard
    .writeText(email)
    .then(() => store.dispatch(showSuccessSnackbar(`Email · ${name} copied`)))
    .catch(() => store.dispatch(showErrorSnackbar("Failed to copy email")))
}

export const CONTACT_DETAILS_COLUMNS: GridColDef<StudentAcademicStatusDTO>[] = [
  {
    field: ColumnKeys.NAME,
    headerName: "Name",
    flex: 1,
    minWidth: 210,
    valueGetter: (value, row) => row.contactDetails.name,
    // Two-line cell: name over email. The email is display-only here — the
    // Email column stays in the chooser for anyone who sorts or exports by it.
    renderCell: (params) => {
      const { name, email } = params.row.contactDetails
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            height: "100%",
            width: "100%",
            minWidth: 0,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Tooltip title={name}>
              <Typography variant="body2" noWrap>
                {name}
              </Typography>
            </Tooltip>
            {email && (
              <Tooltip title={email}>
                {/* display=block keeps the span out of the cell's inline
                    line box, which inherits the grid's 57px line-height and
                    inflates the stack past the row. */}
                <Typography
                  noWrap
                  display="block"
                  variant="body3"
                  color="text.secondary"
                >
                  {email}
                </Typography>
              </Tooltip>
            )}
          </Box>
          {email && (
            <IconButton
              size="small"
              aria-label={`Copy ${name}'s email`}
              onClick={(event) => copyEmail(event, name, email)}
              sx={{
                opacity: 0,
                ".MuiDataGrid-row:hover &": { opacity: 1 },
                "&:focus-visible": { opacity: 1 },
              }}
            >
              <AppIcon
                icon={appIcons.copy06}
                props={{ sx: { width: "16px", height: "16px" } }}
              />
            </IconButton>
          )}
        </Box>
      )
    },
  },
  {
    field: ColumnKeys.EMAIL,
    headerName: "Email",
    width: 200,
    valueGetter: (value, row) => row.contactDetails.email,
  },
  {
    field: ColumnKeys.HUBSPOT_ID,
    headerName: "HubSpot ID",
    width: 200,
    type: "string",
    valueGetter: (value, row) => row.contactDetails.hubspotContactId,
    // Opt-in column: an 11-digit number nobody reads is only worth its width
    // as a control that does what it shows — a link to the contact.
    renderCell: (params) => {
      const hubspotContactId = params.row.contactDetails.hubspotContactId
      if (!hubspotContactId) return null
      return (
        <Link
          href={hubspotContactUrl(hubspotContactId)}
          target="_blank"
          rel="noopener"
          variant="body2"
          onClick={(event) => event.stopPropagation()}
        >
          {hubspotContactId}
        </Link>
      )
    },
  },
]
